import {
  ChangeDetectorRef, Component, Input, OnChanges, OnInit, SimpleChanges
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { cloneDeep, orderBy } from 'lodash-es';
import { filter, map, startWith, switchMap, takeUntil } from 'rxjs/operators';

import {
  EntityCustomIdDetails, EntityCustomIdDetailsFragment, GetEntityById2, GetEntityById2GQL,
  RemoveEntityCustomIdGQL,
  RemoveEntityCustomIdInput,
  UpdateEntityCustomIdGQL, UpdateEntityCustomIdInput
} from 'generated/graphql';

import { WithChangesSubject } from 'core/mixins/with-changes-subject';
import { WithDisposableSubject } from 'core/mixins/with-disposable-subject';
import { ToastrService } from 'core/toastr.service';
import { UpdateCustomIdDialog } from 'entity/dialogs/update-custom-id/update-custom-id.dialog';
import { ConfirmDialogComponent } from 'shared/components/confirm-dialog/confirm-dialog-component';
import { QueryRef } from 'apollo-angular';
import { from } from 'rxjs';

@Component({
  selector: 'app-entity-ids',
  templateUrl: './entity-ids.component.html',
})
export class EntityIdsComponent extends WithDisposableSubject(WithChangesSubject(class { })) implements OnInit, OnChanges {
  @Input() entityId: string;
  @Input() campaignId: string;

  entity: GetEntityById2.GetEntity;

  queryRef: QueryRef<GetEntityById2.Query, GetEntityById2.Variables>;

  customIds: EntityCustomIdDetailsFragment[] = [];
  constructor(
    private _getEntityGQL: GetEntityById2GQL,
    private _cd: ChangeDetectorRef,
    private _toastrService: ToastrService,
    private _dialog: MatDialog,
    private _updateEntityCustomIdGQL: UpdateEntityCustomIdGQL,
    private _removeEntityCustomIdGQL: RemoveEntityCustomIdGQL,
  ) {
    super();
  }

  ngOnInit(): void {
    this.watchForChangeKeys('entityId', 'campaignId').pipe(
      startWith<any, any>(null),
      takeUntil(this.disposer),
      map(() => ({ campaignId: this.campaignId, entityId: this.entityId })),
      filter(({ campaignId, entityId }) => !!campaignId && !!entityId),
      switchMap(({ campaignId, entityId }) => {
        this.queryRef = this._getEntityGQL.watch({
          entityId, campaignId,
          withIds: true,
          withEntityType: false,
          withBestAddress: false,
          withToplines: false,
          withBestPhone: false,
          withBestEmail: false,
        }, {
          fetchPolicy: 'cache-first'
        });

        return this.queryRef.valueChanges;
      }),
      takeUntil(this.disposer),
      map(({ data }) => data?.getEntity)
    ).subscribe((entity) => {
      this.entity = entity;
      const idsCopy =  cloneDeep(
        entity.entityCustomIds.filter(customId => customId.value && customId.customIdType.active)
      );
      this.customIds = orderBy(idsCopy, i => i.customIdType.label.toLowerCase(), ['asc']);

      this._cd.markForCheck();
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    super.ngOnChanges(changes);
  }

  copied() {
    this._toastrService.success('Copied to clipboard');
  }

  // AB-538 Simple Open Dialog
  openDialog(entityCustomId: EntityCustomIdDetailsFragment) {
    UpdateCustomIdDialog.open(this._dialog, {
      data: {
        entityCustomId,
        campaignId: this.campaignId,
        entityId: this.entityId,
      }
    });
  }

  // AB-538 Confirm Dialog component
  openDeleteDialog(customId: EntityCustomIdDetails.Fragment) {
    ConfirmDialogComponent.open(this._dialog, {
      data: {
        message: 'Are you sure you want to remove this Custom ID?'
      }
    }).afterClosed()
      .subscribe(proceed => {
        if (proceed) {

          const input: RemoveEntityCustomIdInput = {
            id: customId.id,
            campaignId: this.campaignId,
          };

          this._removeEntityCustomIdGQL.mutate({ input })
          .pipe(
            switchMap((res) => from(this.queryRef.refetch()).pipe(map(() => res)))
          )
          .subscribe(({data}) => {
              if (data.removeEntityCustomId.success) {
              this._toastrService.success('Custom ID removed');
            }
          });
        }
      });
  }
}
